import {
  Banner,
  Tabs,
  Box,
  BlockStack,
  InlineStack,
  Icon,
  Text,
} from "@shopify/polaris";
import { Modal } from "@shopify/polaris";
import { type ComponentProps, useEffect, useState } from "react";
import { trackEvent } from "../lib/mixpanel";
import { ToggleOnIcon } from "@shopify/polaris-icons";
import { ChatIcon } from "@shopify/polaris-icons";
import { EditIcon } from "@shopify/polaris-icons";
import { ArrowRightIcon } from "@shopify/polaris-icons";
import { isDesktop } from "../lib/isDesktop";
import { useGetStore } from "../api/useGetStore";
import styles from "./AppEnableDisableButton.module.scss";

type IconSource = ComponentProps<typeof Icon>["source"];

function ActionCard({
  icon,
  label,
  description,
  onClick,
  href,
  external,
  glow,
}: {
  icon: IconSource;
  label: string;
  description: string;
  onClick?: () => void;
  href?: string;
  external?: boolean;
  glow?: boolean;
}) {
  const content = (
    <InlineStack align="space-between" blockAlign="center" gap="400">
      <BlockStack gap="100">
        <InlineStack gap="150" blockAlign="center" align="start">
          <Icon source={icon} tone="base" />
          <Text as="p" variant="bodyMd" fontWeight="medium">
            {label}
          </Text>
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">
          {description}
        </Text>
      </BlockStack>
      <Icon source={ArrowRightIcon} tone="base" />
    </InlineStack>
  );

  const className = glow
    ? `${styles.actionCard} ${styles.glow}`
    : styles.actionCard;

  return href ? (
    <a
      href={href}
      target={external ? "_blank" : "_self"}
      rel="noreferrer"
      onClick={onClick}
      className={className}
    >
      {content}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

export function AppEnableDisableButton() {
  const { data, isLoading, isError } = useGetStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("MOBILE");
  const shopName = data ? data.data.shopName : "";
  const qs = new URLSearchParams();
  qs.append("template", "index");
  qs.append(
    "addAppBlockId",
    process.env.REACT_APP_SHOPIFY_API_KEY + "/whatsapp_customer_support_app"
  );
  qs.append("target", "mainSection");
  const automaticAttachLink = `https://${shopName}/admin/themes/current/editor?${qs.toString()}`;

  function onContactSupport() {
    trackEvent("Contact Support Clicked");
  }

  let message = encodeURIComponent(
    `Hi, I am facing issues in adding the widget on my store. Can you help me out?`
  );
  const link = `https://wa.me/+918076623211?text=${message}`;

  const videoLinkMobile = "https://www.youtube.com/embed/C46qy8QWzKo";
  const videoLinkDesktop = `https://www.youtube.com/embed/bqvMpufuA3o`;

  useEffect(() => {
    if (isDesktop) {
      setSelectedTab("DESKTOP");
    } else {
      setSelectedTab("MOBILE");
    }
  }, [setSelectedTab, isDesktop]);

  return (
    <>
      <Modal
        onClose={() => {
          setIsModalOpen(false);
        }}
        open={isModalOpen}
        title="Adding widget on store"
      >
        <Modal.Section>
          <Tabs
            selected={selectedTab === "DESKTOP" ? 0 : 1}
            onSelect={(selectedTab) => {
              if (selectedTab === 0) {
                setSelectedTab("DESKTOP");
              }
              if (selectedTab === 1) {
                setSelectedTab("MOBILE");
              }
            }}
            tabs={[
              {
                id: "DESKTOP",
                content: "Desktop",
                accessibilityLabel: "Desktop",
                panelID: "desktop-content",
              },
              {
                id: "MOBILE",
                content: "Mobile",
                accessibilityLabel: "Mobile",
                panelID: "mobile-content",
              },
            ]}
          />

          <Box paddingBlock="400">
            <iframe
              width="588"
              style={{ width: "100%" }}
              height="472"
              src={`${selectedTab === "DESKTOP" ? videoLinkDesktop : videoLinkMobile}?rel=0&showinfo=0&autoplay=0&mute=0&controls=1`}
              title="YouTube video player"
            ></iframe>
          </Box>

          <Banner
            children="If you are facing any issues in adding the widget. Click on contact support we will help you out."
            action={{
              content: "Contact Support",
              external: true,
              url: link,
              onAction: onContactSupport,
            }}
          />
        </Modal.Section>
      </Modal>
      <InlineStack gap="400" blockAlign="stretch">
        <ActionCard
          icon={ToggleOnIcon}
          label={isDesktop ? "Add Widget to Store" : "Add Widget"}
          description="Add the WhatsApp widget to your store"
          glow
          onClick={() => {
            if (!data || isError) {
              trackEvent("Add Widget to Storefront Error");
              setIsModalOpen(true);
              return;
            }
            window.open(automaticAttachLink, "_top");
            trackEvent("Add Widget to Storefront Clicked");
          }}
        />
        <ActionCard
          icon={EditIcon}
          label={isDesktop ? "Edit Widget" : "Edit"}
          description="Edit settings in the theme editor"
          onClick={() => {
            window.open(automaticAttachLink, "_top");
            trackEvent("Edit Widget Clicked");
          }}
        />
        <ActionCard
          icon={ChatIcon}
          label="Customer Support"
          description="Chat with us for help"
          href={link}
          external
          onClick={onContactSupport}
        />
      </InlineStack>
    </>
  );
}
